import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	formatMediasDateTime,
	getCorrectDate,
} from '../../../../utils/formatDate';
import {
	findAiPhotoResult,
	shouldShowAiResults,
} from '../../../../utils/aiResults';
import { canEditReportPhoto } from '../../../../utils/reportAccess';
import {
	getPhotoSceneLabel,
	getPhotoOriginalUrl,
} from '../../../../utils/reportPhoto';
import { reducerDeletePhotosId } from '../../../../redux/reducer/userSingleReport/reducers/userSingleReport';
import {
	actionDeleteTablePhotos,
	actionRenamePhotoCard,
} from '../../../../redux/action/userSingleReport/actionUserSingleReport';
import { AppDispatch } from '../../../../redux/reducer/store';

import EditPopup from '../../../custom/editPopup/editPopup';
import AiResultCell from '../../../custom/aiResultCell/aiResultCell';
import AiPhotoThumb from '../../../custom/aiPhotoThumb/aiPhotoThumb';
import ArrowIcon from '../../../../assets/icons/chevron.svg';
import EditIcon from '../../../../assets/icons/editicon-2.svg';
import { IReportMediaOptions } from '../../../../types/reportsInterfaces';
import { IUserOptions } from '../../../../types/interfaces';

interface IAdminPhotosOptions {
	setOpenDatePopupIndex: (date: number) => void;
	previousReportIdRef: React.MutableRefObject<string | null>;
	openDatePopupIndex: number;
	setOpenPopupIndex: (date: number) => void;
	setSelectedPhoto: (src: string, original?: boolean) => void;
	openPopupIndex: number;
	scrollModal: () => void;
	namePurpose: string;
	datePurpose: string;
	modalRef: React.RefObject<HTMLDivElement>;
	medias: IReportMediaOptions[];
	title: string;
}

const AdminPhotosTableSection: FC<IAdminPhotosOptions> = ({
	setOpenDatePopupIndex,
	previousReportIdRef,
	openDatePopupIndex,
	setOpenPopupIndex,
	setSelectedPhoto,
	openPopupIndex,
	namePurpose,
	datePurpose,
	scrollModal,
	modalRef,
	medias,
	title,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const { photos, report, aiReview, aiReviewSummary } = useSelector(
		(state: any) => state.userSingleReport.userSingleReport
	);
	const { claims, isCustomer, settings: userSettings } = useSelector(
		(state: IUserOptions) => state.user.user.data
	);
	const showAiResults = shouldShowAiResults(
		isCustomer,
		userSettings?.aiReview,
		claims
	);
	const canEditPhoto = canEditReportPhoto(claims, isCustomer);
	const [newPhotoNameValue, setNewPhotoNameValue] = useState<string>('');
	const [newPhotoDateValue, setNewPhotoDateValue] = useState<Date | null>(null);
	const [isVisibleData, setIsVisibleData] = useState<boolean>(true);
	const [photoIdToDelete, setPhotoIdToDelete] = useState<number | string | null>(
		null
	);

	const photoViewerInstance = document.querySelector('.photoviewer-modal');

	useEffect(() => {
		if (report && report?.reportId?.id) {
			if (previousReportIdRef.current !== report?.reportId?.id) {
				if (photoViewerInstance) {
					photoViewerInstance.remove();
				}
			}
		}
	}, [photoViewerInstance, previousReportIdRef, report]);

	const handlePhotoSelection = (cardId: number) => {
		if (photos.includes(cardId)) {
			dispatch(
				reducerDeletePhotosId(
					photos.filter((selectedCardId: number) => selectedCardId !== cardId)
				)
			);
		} else {
			dispatch(reducerDeletePhotosId([...photos, cardId]));
		}
	};

	const handleAcceptEditOnKeyPress = (
		event: React.KeyboardEvent,
		purpose: string,
		elem?: IReportMediaOptions
	) => {
		if (event.key === 'Enter' && elem) {
			if (purpose === 'photoName' || purpose === 'photoDate') {
				handleClickAcceptRenameBtn(elem);
			}
		}
	};

	const handleClickAcceptRenameBtn = (elem: IReportMediaOptions) => {
		setOpenDatePopupIndex(-1);
		setOpenPopupIndex(-1);
		dispatch(
			actionRenamePhotoCard({
				reportId: report && report.reportId.id,
				medias: {
					media: {
						id: elem.media.id,
					},
					name: newPhotoNameValue || elem?.media?.name,
					date:
						(newPhotoDateValue && getCorrectDate(newPhotoDateValue)) ||
						elem?.date,
					url: elem.url,
				},
			})
		);

		setNewPhotoNameValue('');
		setNewPhotoDateValue(null);
	};

	const handleDeletePhoto = (elem: IReportMediaOptions) => {
		if (!canEditPhoto || !report?.reportId?.id) {
			return;
		}
		dispatch(
			actionDeleteTablePhotos({
				reportId: report.reportId.id,
				medias: [Number(elem.media.id)],
			})
		);
		setPhotoIdToDelete(null);
	};

	const handleChangeReportData = (
		index: number,
		category: string,
		elem?: IReportMediaOptions
	) => {
		const stateToUpdate: any = {
			before: { state: openPopupIndex, setter: setOpenPopupIndex },
			after: {
				state: openPopupIndex,
				setter: setOpenPopupIndex,
			},
			dateBefore: { state: openDatePopupIndex, setter: setOpenDatePopupIndex },
			dateAfter: {
				state: openDatePopupIndex,
				setter: setOpenDatePopupIndex,
			},
		}[category];

		if (stateToUpdate.state === index) {
			return;
		} else {
			stateToUpdate.setter(index);
		}

		if (category.includes('date')) {
			setNewPhotoDateValue(elem?.date ? new Date(elem?.date) : new Date());
		}
	};

	return (
		<div className={`sectionData ${isVisibleData ? 'sectionData_active' : ''}`}>
			<span className='section__title'>{title}</span>
			<button
				className='sectionData_button'
				onClick={() => { setIsVisibleData(!isVisibleData) }}
			>
				<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
			</button>
			<div className='customer__photo_table' >
				{medias.map((elem: IReportMediaOptions, index: number) => {
					const photoAi = showAiResults
						? findAiPhotoResult(aiReview, elem)
						: null;
					const sceneLabel = getPhotoSceneLabel(
						elem,
						aiReview,
						aiReviewSummary
					);

					return (
						<div className='photo__block' key={elem.media.id}>
							<div className='photo__block_row'>
								<input
									disabled={!canEditPhoto}
									onChange={() => handlePhotoSelection(Number(elem.media.id))}
									checked={photos?.includes(elem.media.id) || false}
									type='checkbox'
								/>
								<AiPhotoThumb
									src={elem?.url?.link}
									mediaId={elem.media.id}
									id={elem.media.id.toString()}
									onClick={() => setSelectedPhoto(elem?.url?.link)}
									showOverlay={showAiResults}
									aiReview={aiReview}
								/>
							</div>
							{sceneLabel && (
								<div className='photo__block_row'>
									<span className='photo__scene_text'>Сцена: {sceneLabel}</span>
								</div>
							)}
							<div className='photo__block_row'>
								<span className='photo__merch_text'>
									{report && report.merch.familyName}{' '}
									{report && report.merch.givenName}
								</span>
							</div>
							<div className='photo__block_row photo__block_row-name'>
								<span
									onClick={() => {
										if (canEditPhoto) {
											handleChangeReportData(index, namePurpose);
											setTimeout(scrollModal, 0);
										}
									}}
									className='photo__card_name'
								>
									{elem?.media?.name}
								</span>
								{canEditPhoto && (
									<button
										type='button'
										className='photo__edit_btn'
										onClick={() => {
											handleChangeReportData(index, namePurpose);
											setTimeout(scrollModal, 0);
										}}
										aria-label='Редактировать'
									>
										<img src={EditIcon} alt='' />
									</button>
								)}
								{openPopupIndex === index && (
									<EditPopup
										handleClickAcceptRenameBtn={() =>
											handleClickAcceptRenameBtn(elem)
										}
										handleAcceptOnKeyPress={(event: React.KeyboardEvent) =>
											handleAcceptEditOnKeyPress(event, 'photoName', elem)
										}
										handleClickCancelRenameBtn={() => {
											setOpenPopupIndex(-1);
										}}
										handleChangeValue={(event: React.ChangeEvent<HTMLInputElement>) =>
											setNewPhotoNameValue(event.target.value)
										}
										defaultValue={elem?.media?.name}
										className='rename__card_popup'
										modalRef={modalRef}
									/>
								)}
							</div>
							<div className='photo__block_row'>
								<span>{Object.values(elem.category)}</span>
							</div>
							<div className='photo__block_row'>
								<span
									onClick={() => {
										if (canEditPhoto) {
											handleChangeReportData(index, datePurpose, elem);
											setTimeout(scrollModal, 0);
										}
									}}
									className='photo__card_name'
								>
									{elem.date && formatMediasDateTime(elem.date)}
								</span>
								{openDatePopupIndex === index && (
									<EditPopup
										handleClickAcceptRenameBtn={() =>
											handleClickAcceptRenameBtn(elem)
										}
										handleAcceptOnKeyPress={(event: React.KeyboardEvent) =>
											handleAcceptEditOnKeyPress(event, 'photoDate', elem)
										}
										handleClickCancelRenameBtn={() => {
											setOpenDatePopupIndex(-1);
										}}
										handleChangeValue={(event: Date) =>
											setNewPhotoDateValue(event)
										}
										defaultValue={newPhotoDateValue}
										className='rename__card_popup'
										modalRef={modalRef}
										purpose='photoDate'
									/>
								)}
							</div>
							{showAiResults && photoAi && (
								<div className='photo__block_row photo__block_row-ai'>
									<AiResultCell item={photoAi} />
								</div>
							)}
							<div className='photo__block_row photo__block_row-actions'>
								<button
									type='button'
									className='photo__original_btn'
									onClick={() =>
										setSelectedPhoto(
											getPhotoOriginalUrl(elem) || elem?.url?.link,
											true
										)
									}
								>
									Показать оригинал
								</button>
								{canEditPhoto && (
									photoIdToDelete === elem.media.id ? (
										<div className='photo__delete_confirm'>
											<span>Удалить?</span>
											<button
												type='button'
												className='photo__delete_btn'
												onClick={() => handleDeletePhoto(elem)}
											>
												Да
											</button>
											<button
												type='button'
												className='photo__original_btn'
												onClick={() => setPhotoIdToDelete(null)}
											>
												Нет
											</button>
										</div>
									) : (
										<button
											type='button'
											className='photo__delete_btn'
											onClick={() => setPhotoIdToDelete(elem.media.id)}
										>
											Удалить
										</button>
									)
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default AdminPhotosTableSection;
